import { ReactComponent as AdjustmentsIcon } from '../../assets/admin/Adjustments.svg';
import { ReactComponent as GroupIcon } from '../../assets/admin/Group.svg';
import React, { useState, useEffect, useCallback, useRef } from "react";
import OrderDetailsDialogbox from "./OrderDetailsDialogbox";
import { Eye, Calendar } from "lucide-react";
import { fetchOrderData, getReferralCodes } from "../../actions/OrderAction";
import { getExportInternationalReloadData } from "../../actions/InternationalAction";
import Pagination from "../layout/Pagination";
import { orderStatusOptions, paymentStatusOptions } from "../../constants/Common";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import OrderTable, { TABLE_HEADERS } from "./OrderTable";
import { capitalizeWords } from "../../utils/TextFormat";
import { formatDateParts } from "../../utils/DateFormat";
import { getStatusClass } from "../layout/StatusStyleHandling";
import Loading from "../layout/Loading";
import { isProductListFullyLoaded, loadMoreProducts, loadProductList, PRODUCTLIST } from "../../constants/FetchConstant";
import DropdownBox from "../layout/DropdownBox";
import SearchBar from "../layout/SearchBar";

const OrderTableOverview = ({
  filters: initialFilters,
  setFilters: setParentFilters,
  searchTerm: initialSearchTerm,
  setSearchTerm: setParentSearchTerm,
  isInternational = false,
}) => {
  const [orders, setOrders] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRevenue, setTotalRevenue] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [showMobileFilter, setShowMobileFilter] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [referralCodeList, setReferralCodeList] = useState([
    { _id: "", name: "ALL", code: "" },
    { _id: "NA", name: "N/A", code: "" }
  ]);

  const [tempFilters, setTempFilters] = useState({
    uid: initialFilters.uid || "",
    orderStatus: initialFilters.orderStatus || "",
    paymentStatus: initialFilters.paymentStatus || "",
    productId: initialFilters.productId || "",
    fromDate: initialFilters.fromDate || "",
    toDate: initialFilters.toDate || "",
    referral: initialFilters.referral || "",
  });

  const [searchTerm, setSearchTerm] = useState(initialSearchTerm || "");

  // Column selection
  const [showColumnPopup, setShowColumnPopup] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState(TABLE_HEADERS.map(h => h.key));

  const isInitialMount = useRef(true);

  const handleColumnToggle = (key) => {
    setSelectedColumns((prev) =>
      prev.includes(key)
        ? prev.filter((k) => k !== key)
        : [...prev, key]
    );
  };

  const handleResetColumns = () => {
    setSelectedColumns(TABLE_HEADERS.map(h => h.key));
  };

  const handleApplyColumns = () => {
    setShowColumnPopup(false);
  };

  const columnPopup = (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40" onClick={() => setShowColumnPopup(false)}>
      <div className="bg-white rounded-lg p-6 w-80 max-w-sm shadow-lg" onClick={e => e.stopPropagation()}>
        <h2 className="text-lg font-semibold mb-4">Edit columns</h2>
        <div className="flex flex-col mb-4">
          {TABLE_HEADERS.map((header, idx) => (
            <React.Fragment key={header.key}>
              <label className="flex items-center gap-2 text-base py-2">
                <input
                  type="checkbox"
                  checked={selectedColumns.includes(header.key)}
                  onChange={() => handleColumnToggle(header.key)}
                  className="accent-primary w-4 h-4"
                />
                <span>{header.label}</span>
              </label>
              {idx < TABLE_HEADERS.length - 1 && (
                <hr className="border-t border-gray-200" />
              )}
            </React.Fragment>
          ))}
        </div>
        <div className="flex justify-end gap-2 mt-2">
          <button
            onClick={handleResetColumns}
            className="border border-gray-300 rounded-md px-4 py-2 text-sm font-medium bg-white text-gray-700"
          >
            Reset
          </button>
          <button
            onClick={handleApplyColumns}
            style={{ backgroundColor: '#22C55E' }}
            className="text-white rounded-md px-4 py-2 text-sm font-medium"
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  );

  // Product Dropdown with Search
  const ProductDropdownWithSearch = ({
    value,
    onChange,
    placeholder = "ALL",
  }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState("");
    const [localList, setLocalList] = useState([...PRODUCTLIST]);
    const dropdownRef = useRef(null);
    const listRef = useRef(null);

    // Refresh local list whenever PRODUCTLIST changes
    const refreshList = () => setLocalList([...PRODUCTLIST]);

    useEffect(() => {
      const handleClickOutside = (event) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
          setIsOpen(false);
        }
      };

      const handleEscape = (event) => {
        if (event.key === "Escape") {
          setIsOpen(false);
        }
      };

      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleEscape);

      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
        document.removeEventListener("keydown", handleEscape);
      };
    }, []);

    useEffect(() => {
      if (isOpen) {
        // Load first page when opening if empty
        if (PRODUCTLIST.length === 0) {
          loadProductList(1).then(refreshList);
        } else {
          refreshList();
        }
      }
    }, [isOpen]);

    const handleScroll = async (e) => {
      if (search) return; // Don't paginate during search
      const { scrollTop, scrollHeight, clientHeight } = e.target;
      const nearBottom = scrollHeight - scrollTop <= clientHeight + 40;

      if (nearBottom && !isProductListFullyLoaded()) {
        await loadMoreProducts();
        refreshList();
      }
    };

    const handleSearchChange = (e) => {
      setSearch(e.target.value);
    };

    const handleSelect = (productId) => {
      onChange(productId === "ALL" ? "" : productId);
      setIsOpen(false);
      setSearch("");
    };

    const toggleOpen = (e) => {
      if (e.target.closest && e.target.closest("input")) return;
      setIsOpen((prev) => !prev);
    };

    const selectedProduct = value
      ? localList.find((p) => (p._id || p.id) === value)
      : null;
    const displayValue = selectedProduct ? selectedProduct.name : placeholder;

    const listToShow = search
      ? localList.filter((p) =>
          p.name.toLowerCase().includes(search.toLowerCase())
        )
      : localList;

    const showAllOption = !search || "all".includes(search.toLowerCase());

    return (
      <div ref={dropdownRef} className="relative">
        <button
          type="button"
          onMouseDown={toggleOpen}
          className="w-full flex justify-between items-center px-3 h-[47.6px] text-sm border border-gray-300 rounded-md bg-white focus:outline-none mt-1.3"
        >
          <span className="truncate">{displayValue}</span>
          <svg
            className={`w-4 h-4 transition-transform ${isOpen ? "rotate-180" : ""}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {isOpen && (
          <div
            ref={listRef}
            onScroll={handleScroll}
            className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto"
          >
            <div className="p-2 border-b sticky top-0 bg-white z-10">
              <input
                type="text"
                placeholder="Search product..."
                value={search}
                onChange={handleSearchChange}
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none"
                autoFocus
              />
            </div>

        <div className="py-1">
             {/* Show ALL only if it matches search */}
    {showAllOption && (
      <>
        <button
          onClick={() => handleSelect("ALL")}
          className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 ${
            value === "" ? "bg-gray-50 font-medium" : ""
          }`}
        >
          ALL
        </button>
        <hr className="border-t border-gray-200 my-0" />
      </>
    )}
          

              {/* Product options */}
              {listToShow.length > 0 ? (
                listToShow.map((p, index) => (
                  <div key={p._id || p.id}>
                    <button
                      onClick={() => handleSelect(p._id || p.id)}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 ${
                        value === (p._id || p.id)
                          ? "bg-gray-50 font-medium"
                          : ""
                      }`}
                    >
                      {p.name}
                    </button>
                    {index < listToShow.length - 1 && (
                      <hr className="border-t border-gray-200 my-0" />
                    )}
                  </div>
                ))
              ) : (
                <div className="px-3 py-2 text-sm text-gray-500 italic">
                  No products match "{search}"
                </div>
              )}

              {!search && !isProductListFullyLoaded() && (
                <div className="px-3 py-2 text-xs text-gray-400 text-center">
                  Scroll for more...
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Fetch Orders Data
  const fetchOrdersData = useCallback(
    async (searchTermParam, filtersParam, pageParam,limitParam
      =itemsPerPage
    ) => {
      try {
        setLoading(true);

        let referralValue = filtersParam.referral || "";
        let hasReferral = undefined;

        if (filtersParam.referral === "NA") {
          hasReferral = "false";
          referralValue = "";
        } else if (filtersParam.referral && filtersParam.referral !== "") {
          referralValue = filtersParam.referral;
          hasReferral = "true";
        }

        const requestData = {
          filters: {
            uid: filtersParam.uid || "",
            orderStatus: filtersParam.orderStatus || "",
            paymentStatus: filtersParam.paymentStatus || "",
            productId: filtersParam.productId || "",
            fromDate: filtersParam.fromDate || "",
            toDate: filtersParam.toDate || "",
            referral: referralValue,
            ...(hasReferral !== undefined && { hasReferral }),
            operator: filtersParam.operator || "",
          },
          search: searchTermParam,
          pagination: { page: pageParam, limit: limitParam }
        };

        const response = isInternational
          ? await getExportInternationalReloadData(requestData, searchTermParam)
          : await fetchOrderData(requestData);

        if (response.success) {
          setOrders(response.data || []);
          setTotalPages(response.totalPages || 1);
          setTotalCount(response.totalCount || 0);
          setTotalRevenue(response.totalRevenue || 0);
        } else {
          setOrders([]);
          setTotalPages(1);
          setTotalCount(0);
          setTotalRevenue(0);
          toast.error(response?.message || "Failed to fetch order data");
        }
      } catch (error) {
        setOrders([]);
        setTotalPages(1);
        setTotalCount(0);
        setTotalRevenue(0);
        toast.error("Something went wrong while fetching orders. Please try again.");
      } finally {
        setLoading(false);
      }
    },
    [itemsPerPage, isInternational] // Removed referralCodeList
  );


  useEffect(() => {
    const initializeData = async () => {
      setLoading(true);
      try {
        // 1. Fetch referral codes
        const referralResponse = await getReferralCodes();
        if (referralResponse.success) {
          const formattedCodes = referralResponse.referralCodes.map(ref => ({
            _id: ref.id,
            name: ref.code.toString(),
            code: ref.code.toString(),
          }));
          setReferralCodeList([
            { _id: "", name: "ALL", code: "" },
            { _id: "NA", name: "N/A", code: "" },
            ...formattedCodes
          ]);
        }

        // 2. Set initial tempFilters
        const initFilters = {
          uid: initialFilters.uid || "",
          orderStatus: initialFilters.orderStatus || "",
          paymentStatus: initialFilters.paymentStatus || "",
          productId: initialFilters.productId || "",
          fromDate: initialFilters.fromDate || "",
          toDate: initialFilters.toDate || "",
          referral: initialFilters.referral || initialFilters.referralId || "",
          mobileNumber: initialFilters.mobileNumber || "",
          operator: initialFilters.operator || "",
        };
        setTempFilters(initFilters);

        // 3. Fetch orders ONCE
        await fetchOrdersData(initialSearchTerm || "", initFilters, 1);
      } catch (error) {
        toast.error("Failed to load initial data");
      } finally {
        setLoading(false);
        isInitialMount.current = false;
      }
    };

    initializeData();
  }, []); // Run only once

  // Sync tempFilters when initialFilters change (e.g. parent updates)
  useEffect(() => {
    setTempFilters(prev => ({
      ...prev,
      uid: initialFilters.uid || "",
      orderStatus: initialFilters.orderStatus || "",
      paymentStatus: initialFilters.paymentStatus || "",
      productId: initialFilters.productId || "",
      fromDate: initialFilters.fromDate || "",
      toDate: initialFilters.toDate || "",
      referral: initialFilters.referral || initialFilters.referralId || "",
      mobileNumber: initialFilters.mobileNumber || "",
      operator: initialFilters.operator || "",
    }));
  }, [initialFilters]);

  // Handlers
  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchOrdersData(searchTerm.trim(), tempFilters, page);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
    fetchOrdersData(searchTerm.trim(), tempFilters, 1,newItemsPerPage);
  };

  const handleTempChange = (e) => {
    const { name, value } = e.target;
    setTempFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleTempFromDateChange = (e) => {
    setTempFilters((prev) => ({ ...prev, fromDate: e.target.value }));
  };

  const handleTempToDateChange = (e) => {
    setTempFilters((prev) => ({ ...prev, toDate: e.target.value }));
  };

  const handleSearchTermChange = (newSearchTerm) => {
    setSearchTerm(newSearchTerm);
    setParentSearchTerm(newSearchTerm);
    setCurrentPage(1);
    fetchOrdersData(newSearchTerm.trim(), tempFilters, 1);
  };

  const openModal = (order) => {
    setSelectedOrder(order);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedOrder(null);
  };

  const updateReferralCode = (id, newReferralCode) => {
    setOrders((prev) =>
      prev.map((order) =>
        order._id === id
          ? {
              ...order,
              customer: {
                ...order.customer,
                referral: {
                  ...order.customer?.referral,
                  code: newReferralCode,
                },
              },
            }
          : order
      )
    );
  };

  const applyFilters = () => {
    if (!validateDateRange(tempFilters.fromDate, tempFilters.toDate)) return;

    setParentFilters(tempFilters);
    setCurrentPage(1);
    fetchOrdersData(searchTerm.trim(), tempFilters, 1, itemsPerPage);
    setShowMobileFilter(false);
  };

  const resetFilters = () => {
    const emptyFilters = {
      uid: "",
      orderStatus: "",
      paymentStatus: "",
      productId: "",
      fromDate: "",
      toDate: "",
      referral: "",
      operator: "",
    };
    setSearchTerm("");
    setParentSearchTerm("");
    setTempFilters(emptyFilters);
    setParentFilters(emptyFilters);
    setCurrentPage(1);
    fetchOrdersData("", emptyFilters, 1);
    setShowMobileFilter(false);
  };

  const validateDateRange = (fromDate, toDate) => {
    if (!fromDate || !toDate) return true;
    const from = new Date(fromDate);
    const to = new Date(toDate);
    if (isNaN(from.getTime()) || isNaN(to.getTime())) {
      toast.error("Invalid date format");
      return false;
    }
    if (from > to) {
      toast.error("From date must be before or equal to the To date");
      return false;
    }
    return true;
  };

  const mobileFilterPopup = (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40" onClick={() => setShowMobileFilter(false)}>
      <div className="bg-white rounded-lg p-4 w-11/12 max-w-sm" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-lg font-semibold mb-4">Filter Orders</h2>
        <div className="w-full mb-3">
          <label className="primary_label">Referral Code</label>
          <DropdownBox
            optionList={referralCodeList}
            styleName="w-full text-sm font-normal text-text1 focus:outline-none"
            value={tempFilters.referral}
            placeholder={tempFilters.referral !== undefined ? (referralCodeList.find((ref) => ref._id === tempFilters.referral)?.name || "SELECT") : "SELECT"}
            displayKey="name"
            valueKey="_id"
            isObjectData={true}
            handleChange={(selectedValue) => {
              handleTempChange({ target: { name: "referral", value: selectedValue } });
            }}
            emptyStringName="ALL"
            searchable={true}
          />
        </div>
        <div className="mb-3">
          <label className="primary_label">Order Status</label>
          <DropdownBox
            optionList={["ALL", ...orderStatusOptions]}
            styleName="w-full text-sm font-normal text-text1 focus:outline-none"
            value={tempFilters.orderStatus || ""}
            placeholder={tempFilters.orderStatus ? tempFilters.orderStatus : "ALL"}
            handleChange={(selectedValue) =>
              handleTempChange({
                target: { name: "orderStatus", value: selectedValue === "ALL" ? "" : selectedValue },
              })
            }
            emptyStringName="ALL"
            searchable={true}
          />
        </div>
        <div className="mb-3">
          <label className="primary_label">Payment Status</label>
          <DropdownBox
            optionList={["ALL", ...paymentStatusOptions]}
            styleName="w-full text-sm font-normal text-text1 focus:outline-none"
            value={tempFilters.paymentStatus || ""}
            placeholder={tempFilters.paymentStatus ? tempFilters.paymentStatus : "ALL"}
            handleChange={(selectedValue) =>
              handleTempChange({
                target: { name: "paymentStatus", value: selectedValue === "ALL" ? "" : selectedValue },
              })
            }
            emptyStringName="ALL"
            searchable={true}
          />
        </div>
        <div className="mb-3">
          <label className="primary_label">Select Product</label>
          <ProductDropdownWithSearch
            value={tempFilters.productId || ""}
            onChange={(selectedValue) =>
              handleTempChange({
                target: { name: "productId", value: selectedValue }
              })
            }
            placeholder="ALL"
          />
        </div>
        <div className="mb-3">
          <label className="primary_label">From Date</label>
          <input type="date" className="primary_input" value={tempFilters.fromDate} onChange={handleTempFromDateChange} max={tempFilters.toDate} />
        </div>
        <div className="mb-3">
          <label className="primary_label">To Date</label>
          <input type="date" className="primary_input" value={tempFilters.toDate} onChange={handleTempToDateChange} min={tempFilters.fromDate} />
        </div>
        <div className="flex gap-2 mt-4">
          <button onClick={resetFilters} className="secondary_button inactive flex-1 text-[12px] sm:text-sm">Reset</button>
          <button onClick={applyFilters} className="secondary_button active flex-1 text-[12px] sm:text-sm">Apply</button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="bg-white px-4 sm:px-10">
      {/* Mobile View */}
      <div className="block md:hidden">
        <div className="flex flex-col gap-3 pt-4">
          <div className="flex flex-row items-center w-full gap-2">
            <div className="flex-1 flex items-center h-9">
              <SearchBar
                searchTerm={searchTerm}
                onChange={setSearchTerm}
                onSearch={() => fetchOrdersData(searchTerm.trim(), tempFilters, 1)}
                className="w-full"
              />
            </div>
            <div className="flex items-center">
              <button
                className="flex items-center justify-center h-9 p-0"
                aria-label="Group"
                style={{
                  minWidth: 40,
                  borderRadius: '6px',
                  border: '1px solid #E5E7EB',
                  background: '#FFF',
                  boxShadow: '0 1px 2px 0 rgba(0,0,0,0.05)'
                }}
                onClick={() => setShowColumnPopup(true)}
              >
                <GroupIcon className="w-6 h-6 text-gray-500" />
              </button>
            </div>
            <div className="flex items-center">
              <button
                className="flex items-center justify-center h-9 p-0"
                aria-label="Filter"
                style={{
                  minWidth: 40,
                  borderRadius: '6px',
                  border: '1px solid #E5E7EB',
                  background: '#FFF',
                  boxShadow: '0 1px 2px 0 rgba(0,0,0,0.05)'
                }}
                onClick={() => setShowMobileFilter(true)}
              >
                <AdjustmentsIcon className="w-6 h-6 text-gray-500" />
              </button>
            </div>
          </div>
          <div className="flex gap-2">
            <div className="border border-gray-200 rounded-lg flex-1">
              <div className="flex p-2">
                <div className="px-2 gap-2">
                  <div className="text-gray-500 text-[12px] uppercase font-medium w-fit mb-2">
                    TOTAL TRANSACTIONS
                  </div>
                  <div className="text-[18px] font-bold text-text1">{totalCount}</div>
                </div>
                <div className="px-2 border-l border-gray-200">
                  <div className="text-gray-500 text-[12px] uppercase font-medium mb-2">
                    TOTAL REVENUE
                  </div>
                  <div className="text-[18px] font-bold text-text1">
                    € {Number(totalRevenue || 0).toFixed(2)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="rounded-lg border-t border-x border-b border-gray-300 overflow-x-auto mt-6">
          <div className="mt-0 flex flex-col gap-0">
            {orders && orders.length > 0 ? (
              orders.map((order) => (
                <table key={order._id} className="w-full border border-gray-200 rounded-none bg-white shadow-sm">
                  <tbody>
                    {TABLE_HEADERS.filter(header => selectedColumns.includes(header.key)).map((header) => (
                      <tr key={header.key}>
                        <td className="py-2 px-2 text-[16px] font-medium  text-gray-900 w-1/2 align-top">
                          {header.label}
                        </td>
                        <td className="py-2 px-2 text-[16px] font-normal  text-gray-700 w-1/2 align-top break-words max-w-[140px] overflow-hidden text-ellipsis">
                          {header.key === "createdAt" ? (
                            (() => {
                              const { date, time } = formatDateParts(order[header.key]);
                              return (
                                <div className="whitespace-nowrap">
                                  <div>{date}</div>
                                  <div>{time}</div>
                                </div>
                              );
                            })()
                          ) : header.isAction ? (
                            <button
                              onClick={() => openModal(order)}
                              className="flex items-center justify-center p-2 bg-gray-100 rounded-md"
                            >
                              <Eye className="h-5 w-5 text-gray-400" />
                            </button>
                          ) : header.isStatus ? (
                            <span className={getStatusClass(order[header.key])} style={{ width: "120px", minWidth: "120px", maxWidth: "120px", display: "block" }}>
                              {!header.isPaymentStatus ? capitalizeWords(order[header.key]) : order[header.key] || ""}
                            </span>
                          ) : header.key === "total.totalAmount" ? (
                            (order.total?.totalAmount?.toFixed(2) ?? "0.00")
                          ) : header.key === "customer.referral.code" ? (
                            order.customer?.referral?.code?.toString() ?? "N/A"
                          ) : (
                            header.key.split(".").reduce((o, key) => (o ? o[key] : ""), order) || "N/A"
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ))
            ) : loading ? (
              <div className="text-center text-gray-500 p-4 bg-white border border-gray-200 rounded-lg">Loading...</div>
            ) : (
              <div className="text-center text-gray-500 p-4 bg-white border border-gray-200 rounded-lg">No orders found</div>
            )}
          </div>
        </div>
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalCount={totalCount}
          itemsPerPage={itemsPerPage}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
        />
        {showMobileFilter && mobileFilterPopup}
        {showColumnPopup && columnPopup}
        {isModalOpen && selectedOrder && (
          <OrderDetailsDialogbox
            selectedOrder={selectedOrder}
            closeModal={closeModal}
            updateOrderStatus={(id, newStatus) => {
              setOrders((prev) =>
                prev.map((order) =>
                  order._id === id
                    ? { ...order, orderStatus: newStatus }
                    : order
                )
              );
              setSelectedOrder((prev) =>
                prev && prev._id === id
                  ? { ...prev, orderStatus: newStatus }
                  : prev
              );
            }}
            updatePaymentStatus={(id, newStatus) => {
              setOrders((prev) =>
                prev.map((order) =>
                  order._id === id
                    ? { ...order, paymentStatus: newStatus }
                    : order
                )
              );
              setSelectedOrder((prev) =>
                prev && prev._id === id
                  ? { ...prev, paymentStatus: newStatus }
                  : prev
              );
            }}
            updateReferralCode={updateReferralCode}
          />
        )}
      </div>

      {/* Desktop View */}
      <div className="hidden md:block">
        <div>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-3 gap-4">
            <div className="w-full">
              <label className="primary_label">Referral Code</label>
              <DropdownBox
                optionList={referralCodeList}
                styleName="w-full text-sm font-normal text-text1 focus:outline-none"
                value={tempFilters.referral}
                placeholder={tempFilters.referral !== undefined ? (referralCodeList.find((ref) => ref._id === tempFilters.referral)?.name || "SELECT") : "SELECT"}
                displayKey="name"
                valueKey="_id"
                isObjectData={true}
                handleChange={(selectedValue) => {
                  handleTempChange({ target: { name: "referral", value: selectedValue } });
                }}
                emptyStringName="ALL"
                searchable={true}
              />
            </div>
            <div>
              <label className="primary_label">Order Status</label>
              <DropdownBox
                optionList={["ALL", ...orderStatusOptions]}
                styleName="w-full text-sm font-normal text-text1 focus:outline-none"
                value={tempFilters.orderStatus || ""}
                placeholder={tempFilters.orderStatus ? tempFilters.orderStatus : "ALL"}
                handleChange={(selectedValue) =>
                  handleTempChange({
                    target: { name: "orderStatus", value: selectedValue === "ALL" ? "" : selectedValue },
                  })
                }
                emptyStringName="ALL"
                searchable={true}
              />
            </div>
            <div>
              <label className="primary_label">Payment Status</label>
              <DropdownBox
                optionList={["ALL", ...paymentStatusOptions]}
                styleName="w-full text-sm font-normal text-text1 focus:outline-none"
                value={tempFilters.paymentStatus || ""}
                placeholder={tempFilters.paymentStatus ? tempFilters.paymentStatus : "ALL"}
                handleChange={(selectedValue) =>
                  handleTempChange({
                    target: { name: "paymentStatus", value: selectedValue === "ALL" ? "" : selectedValue },
                  })
                }
                emptyStringName="ALL"
                searchable={true}
              />
            </div>
            <div>
              <label className="primary_label">Product Name</label>
              <ProductDropdownWithSearch
                value={tempFilters.productId || ""}
                onChange={(selectedValue) =>
                  handleTempChange({
                    target: { name: "productId", value: selectedValue }
                  })
                }
                placeholder="ALL"
              />
            </div>
            <div className="flex-1">
              <label htmlFor="FromDate" className="primary_label">From Date</label>
              <div className="relative mt-1">
                <div className="absolute inset-y-0 left-0 px-3 bg-gray-50 border border-gray-300 rounded-tl-md rounded-bl-md flex items-center">
                  <Calendar className="text-md text-gray-500 h-5 w-5" />
                </div>
                <div className="pl-10">
                  <input
                    type="date"
                    id="FromDate"
                    name="FromDate"
                    className="w-full text-text1 text-sm py-3 px-5 border rounded-md border-gray-300 focus:outline-none focus:ring-1 focus:ring-gray-300 appearance-none date-input-no-indicator"
                    value={tempFilters.fromDate}
                    onChange={handleTempFromDateChange}
                    max={tempFilters.toDate}
                  />
                </div>
              </div>
            </div>
            <div className="flex-1">
              <label htmlFor="ToDate" className="primary_label">To Date</label>
              <div className="relative mt-1">
                <div className="absolute inset-y-0 left-0 px-3 bg-gray-50 border border-gray-300 rounded-tl-md rounded-bl-md flex items-center">
                  <Calendar className="text-md text-gray-500 h-5 w-5" />
                </div>
                <div className="pl-10">
                  <input
                    type="date"
                    id="ToDate"
                    name="ToDate"
                    className="w-full text-text1 text-sm py-3 px-5 border rounded-md border-gray-300 focus:outline-none focus:ring-1 focus:ring-gray-300 appearance-none date-input-no-indicator"
                    value={tempFilters.toDate}
                    onChange={handleTempToDateChange}
                    min={tempFilters.fromDate}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="mt-4 gap-3 flex justify-end items-center">
            <button onClick={resetFilters} className="secondary_button inactive text-[12px] sm:text-sm" disabled={loading}>Reset</button>
            <button onClick={applyFilters} className="secondary_button active text-[12px] sm:text-sm" disabled={loading}>
              <span className={`${loading ? "px-0" : "px-4"}`}>{loading ? "Applying.." : "Apply"}</span>
            </button>
          </div>
        </div>
        <hr className="mb-10 border-gray-300 mt-4" />
        {loading ? (
          <Loading />
        ) : (
          <div>
            <OrderTable
              orders={orders}
              searchTerm={searchTerm}
              setSearchTerm={handleSearchTermChange}
              totalCount={totalCount}
              totalRevenue={totalRevenue}
              openModal={openModal}
              selectedOrder={selectedOrder}
              isModalOpen={isModalOpen}
              closeModal={closeModal}
              updateOrderStatus={(id, newStatus) => {
                setOrders((prev) =>
                  prev.map((order) =>
                    order._id === id
                      ? { ...order, orderStatus: newStatus }
                      : order
                  )
                );
                setSelectedOrder((prev) =>
                  prev && prev._id === id
                    ? { ...prev, orderStatus: newStatus }
                    : prev
                );
              }}
              updatePaymentStatus={(id, newStatus) => {
                setOrders((prev) =>
                  prev.map((order) =>
                    order._id === id
                      ? { ...order, paymentStatus: newStatus }
                      : order
                  )
                );
                setSelectedOrder((prev) =>
                  prev && prev._id === id
                    ? { ...prev, paymentStatus: newStatus }
                    : prev
                );
              }}
            />
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalCount={totalCount}
              itemsPerPage={itemsPerPage}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handleItemsPerPageChange}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderTableOverview;
